/**
 * How the review writes, for whoever is looking (session 55, `.docs/55-mockup-comments.md`):
 *
 *   - **a guest** through the link's own route, under the name they gave;
 *   - **a member** through their client's routes, as themselves;
 *   - **the studio** through the tools, as the studio page always has.
 *
 * One shape for the three, so the review's card and list never ask who is looking — only what
 * the server said they may do (`viewer.may_*`). Each door's answer refreshes the review's read,
 * because every one of them is under the `mockups` module's keys.
 */

import type { MockupLinkViewer, MockupPin } from '@fareast/shared'

import {
  useCommentOnMockup,
  useDecideMockupThread,
  useGuestComment,
} from '../../api/client/index.js'
import { useStudioAction } from '../../api/studio.js'

export type NewComment = {
  readonly version: number
  readonly body: string
  readonly pin?: MockupPin | undefined
  /** The thread it answers. */
  readonly reply_to?: string | undefined
  /** A guest's name — asked of a guest, and of nobody else. */
  readonly name?: string | undefined
}

export type ReviewDoors = {
  readonly comment: (comment: NewComment) => Promise<void>
  readonly resolve: (id: string) => Promise<void>
  readonly reopen: (id: string) => Promise<void>
}

export function useReviewDoors(publicId: string, viewer: MockupLinkViewer): ReviewDoors {
  const guest = useGuestComment()
  const member = useCommentOnMockup()
  const decide = useDecideMockupThread()
  const studioComment = useStudioAction('mockups_comment')
  const studioResolve = useStudioAction('mockups_resolve_comment')
  const studioReopen = useStudioAction('mockups_reopen_comment')

  switch (viewer.kind) {
    case 'guest':
      return {
        comment: async ({ name, ...comment }) => {
          await guest.mutateAsync({ publicId, ...comment, name: name ?? '' })
        },
        resolve: refused,
        reopen: refused,
      }
    case 'member':
      return {
        comment: async ({ name: _name, ...comment }) => {
          await member.mutateAsync({ ref: viewer.ref, ...comment })
        },
        resolve: async (id) => {
          await decide.mutateAsync({ ref: viewer.ref, id, decision: 'resolve' })
        },
        reopen: async (id) => {
          await decide.mutateAsync({ ref: viewer.ref, id, decision: 'reopen' })
        },
      }
    case 'studio':
      return {
        comment: async ({ name: _name, ...comment }) => {
          await studioComment.mutateAsync({ mockup: viewer.ref, ...comment })
        },
        resolve: async (id) => {
          await studioResolve.mutateAsync({ comment_id: id })
        },
        reopen: async (id) => {
          await studioReopen.mutateAsync({ comment_id: id })
        },
      }
  }
}

/** A guest has no door to resolve by: the review never offers one (`may_resolve`). */
function refused(): Promise<void> {
  return Promise.reject(new Error('Only the studio and the client resolve a thread.'))
}
